import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  VideoLibrary,
  Inventory2,
  People,
  CheckCircle,
  HourglassTop,
  Cloud,
  CloudOff,
} from "@mui/icons-material";
import { dashboardAPI } from "../../services/adminApi";

interface DashboardData {
  videos: {
    total: number;
    ready: number;
    processing: number;
    error: number;
  };
  products: {
    total: number;
    active: number;
    draft: number;
  };
  users: {
    total: number;
  };
  recentVideos: any[];
  recentProducts: any[];
  cloudflareConfigured: boolean;
}

const CARD_COLORS = {
  purple: { bg: "rgba(21,101,192,0.08)",  icon: "#1565C0" },  // Dr.Paw blue
  green:  { bg: "rgba(0,137,123,0.08)",   icon: "#00897B" },  // teal
  blue:   { bg: "rgba(66,165,245,0.10)",  icon: "#1976D2" },  // blue mid
  orange: { bg: "rgba(123,31,162,0.08)",  icon: "#7B1FA2" },  // purple accent
};

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await dashboardAPI.getOverview();
      const body = res.data ?? {};
      setData({
        videos:               body.videos               ?? { total: 0, ready: 0, processing: 0, error: 0 },
        products:             body.products             ?? { total: 0, active: 0, draft: 0 },
        users:                body.users                ?? { total: 0 },
        recentVideos:         body.recentVideos         ?? [],
        recentProducts:       body.recentProducts       ?? [],
        cloudflareConfigured: body.cloudflareConfigured ?? false,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 6, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={isMobile ? 120 : 140} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="warning">No dashboard data available.</Alert>;
  }

  return (
    <Box>
      {/* Cloudflare Status */}
      {!data.cloudflareConfigured && (
        <Alert
          severity="warning"
          icon={<CloudOff />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <strong>Cloudflare Stream not configured.</strong> The system is using
          simulated responses. Add your API credentials to{" "}
          <code>.env</code> to connect.
        </Alert>
      )}

      {data.cloudflareConfigured && (
        <Alert
          severity="success"
          icon={<Cloud />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <strong>Cloudflare Stream connected.</strong> Videos are being streamed
          from Cloudflare.
        </Alert>
      )}

      {/* Stat Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 1.5, sm: 2 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  bgcolor: CARD_COLORS.purple.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VideoLibrary sx={{ color: CARD_COLORS.purple.icon }} />
              </Box>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: "1.5rem", sm: "2rem" } }}>
                  {data.videos.total || 0}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 500, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  Videos
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "center", sm: "flex-start" } }}>
              <Chip
                icon={<CheckCircle sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                label={isMobile ? data.videos.ready : `${data.videos.ready || 0} Ready`}
                size="small"
                sx={{ bgcolor: "#ECFDF5", color: "#10B981", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
              <Chip
                icon={<HourglassTop sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                label={isMobile ? data.videos.processing : `${data.videos.processing || 0} Processing`}
                size="small"
                sx={{ bgcolor: "#FFF7ED", color: "#F59E0B", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 1.5, sm: 2 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  bgcolor: CARD_COLORS.green.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Inventory2 sx={{ color: CARD_COLORS.green.icon }} />
              </Box>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: "1.5rem", sm: "2rem" } }}>
                  {data.products.total || 0}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 500, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  Products
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "center", sm: "flex-start" } }}>
              <Chip
                label={isMobile ? data.products.active : `${data.products.active || 0} Active`}
                size="small"
                sx={{ bgcolor: "#ECFDF5", color: "#10B981", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
              <Chip
                label={isMobile ? data.products.draft : `${data.products.draft || 0} Draft`}
                size="small"
                sx={{ bgcolor: "#F3F4F6", color: "#6B7280", fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  bgcolor: CARD_COLORS.blue.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <People sx={{ color: CARD_COLORS.blue.icon }} />
              </Box>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: "1.5rem", sm: "2rem" } }}>
                  {data.users.total || 0}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 500, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  Users
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.04)",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, sm: 2 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  bgcolor: data.cloudflareConfigured
                    ? CARD_COLORS.green.bg
                    : CARD_COLORS.orange.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {data.cloudflareConfigured ? (
                  <Cloud sx={{ color: CARD_COLORS.green.icon }} />
                ) : (
                  <CloudOff sx={{ color: CARD_COLORS.orange.icon }} />
                )}
              </Box>
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: "0.9rem", sm: "1.25rem" } }}>
                  {data.cloudflareConfigured ? "Connected" : "Simulated"}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 500, fontSize: { xs: "0.65rem", sm: "0.875rem" } }}
                >
                  {isMobile ? "CF Stream" : "Cloudflare Stream"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Items */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.04)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                Recent Videos
              </Typography>
            </Box>
            <List disablePadding>
              {data.recentVideos.map((video: any) => (
                <ListItem
                  key={video._id}
                  divider
                  sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                    <VideoLibrary
                      fontSize="small"
                      sx={{ color: "#1565C0" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={video.title}
                    secondary={`${formatDuration(video.duration)} Â· ${video.visibility}`}
                    primaryTypographyProps={{ 
                      fontWeight: 600, 
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      noWrap: true,
                      sx: { maxWidth: { xs: 140, sm: 200 } },
                    }}
                    secondaryTypographyProps={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                  />
                  <Chip
                    label={video.status}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.6rem", sm: "0.7rem" },
                      height: { xs: 22, sm: 24 },
                      bgcolor:
                        video.status === "ready"
                          ? "#ECFDF5"
                          : video.status === "processing"
                          ? "#FFF7ED"
                          : "#FEF2F2",
                      color:
                        video.status === "ready"
                          ? "#10B981"
                          : video.status === "processing"
                          ? "#F59E0B"
                          : "#EF4444",
                    }}
                  />
                </ListItem>
              ))}

              {(!data.recentVideos || data.recentVideos.length === 0) && (
                <ListItem sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                  <ListItemText
                    primary="No videos yet"
                    primaryTypographyProps={{
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              borderRadius: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              border: "1px solid rgba(0,0,0,0.04)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                Recent Products
              </Typography>
            </Box>
            <List disablePadding>
              {data.recentProducts.map((product: any) => (
                <ListItem
                  key={product._id}
                  divider
                  sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 32, sm: 36 } }}>
                    <Inventory2
                      fontSize="small"
                      sx={{ color: "#10B981" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={product.name}
                    secondary={`${product.type} Â· $${product.price}`}
                    primaryTypographyProps={{ 
                      fontWeight: 600, 
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      noWrap: true,
                      sx: { maxWidth: { xs: 140, sm: 200 } },
                    }}
                    secondaryTypographyProps={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}
                  />
                  <Chip
                    label={product.status}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.6rem", sm: "0.7rem" },
                      height: { xs: 22, sm: 24 },
                      bgcolor:
                        product.status === "active" ? "#ECFDF5" : "#F3F4F6",
                      color:
                        product.status === "active" ? "#10B981" : "#6B7280",
                    }}
                  />
                </ListItem>
              ))}

              {(!data.recentProducts || data.recentProducts.length === 0) && (
                <ListItem sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                  <ListItemText
                    primary="No products yet"
                    primaryTypographyProps={{
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;



